import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowRight, Compass, Heart, Eye, Target, Lightbulb, CheckCircle2 } from "lucide-react";
import API from "@/lib/api";

const ICONS = { Heart, Lightbulb, Compass, Eye, Target };

const FALLBACK = {
  hero: {
    overline: "About NGXP",
    title_pre: "Built to Engineer the Future of ",
    title_highlight: "Intelligent Operations",
    title_post: "",
    paragraph1:
      "NGXP Technologies is a future-focused technology company helping enterprises transform complex operational challenges into intelligent, scalable, and business-driven solutions.",
    paragraph2:
      "We are built for organizations that want more than technology delivery – they want a partner capable of understanding complexity, engineering intelligently, and building systems that create long-term operational value.",
    cta_primary_label: "Explore Our Capabilities",
    cta_primary_link: "/services",
    cta_secondary_label: "Start a Strategic Discussion",
    cta_secondary_link: "/contact#contact-form",
    image_url: "/2.gif",
  },
  "who-we-are": {
    overline: "Who We Are",
    title_pre: "Next Generation ",
    title_highlight: "eXPerience",
    paragraphs: [
      "NGXP Technologies, also known as Next Generation eXPerience, is a technology company founded with a clear purpose: to build advanced systems that help organizations operate smarter, faster, safer, and more efficiently.",
      "Headquartered in Kerala, India, NGXP brings together a multidisciplinary team across artificial intelligence, enterprise systems, data engineering, IoT, embedded systems, 3D, simulation, XR, digital twin development, and enterprise solution delivery.",
      "We work with organizations that face complex operational challenges – where visibility, speed, safety, productivity, decision-making, and long-term scalability matter.",
    ],
    callout:
      "NGXP exists to turn complex business and operational problems into intelligent, practical, and future-ready systems.",
  },
  origin: {
    overline: "Our Origin",
    title_pre: "Why NGXP Was ",
    title_highlight: "Created",
    paragraphs: [
      "NGXP was founded on the belief that the next generation of enterprise growth will not come from isolated software systems alone.",
      "Modern organizations need technology partners who can understand real-world operations, connect business logic with engineering, and build systems that help teams make better decisions.",
      "From the beginning, NGXP has been shaped by one ambition:",
    ],
    quote:
      "to build technology that does not simply support business operations, but helps improve them.",
    closing: "That belief continues to guide how we think, design, engineer, and deliver.",
  },
  "vision-mission": {
    vision_title_pre: "Our ",
    vision_title_highlight: "Vision",
    vision_paragraphs: [
      "To become a globally respected technology company that engineers intelligent systems for the next generation of enterprise and industrial operations.",
      "We envision a future where organizations are not limited by delayed reports, disconnected tools, manual decisions, and static systems.",
      "Instead, they operate with clarity, intelligence, automation, simulation, and real-time decision support.",
    ],
    vision_footer: "NGXP is working toward that future.",
    mission_title_pre: "Our ",
    mission_title_highlight: "Mission",
    mission_paragraph:
      "Our mission is to help organizations solve complex operational challenges through intelligent, scalable, and practical technology systems.",
    mission_label: "We focus on building solutions that improve:",
    mission_items: [
      "Real-time visibility", "Operational control", "Decision-making speed", "Productivity",
      "Safety awareness", "Workflow efficiency", "Training effectiveness", "Business intelligence",
      "Long-term scalability", "Return on technology investment",
    ],
    mission_footer:
      "We do not approach technology as a checklist of features. We approach it as a business transformation tool.",
  },
  believe: {
    overline: "What We Believe",
    title_pre: "Technology should be ",
    title_highlight: "clear, useful, intelligent,",
    title_post: " and measurable.",
    cards: [
      { icon: "Heart", text: "A system should not be built only to exist. It should help people work better, leaders decide faster, teams reduce errors, and organizations grow stronger." },
      { icon: "Lightbulb", text: "The best enterprise systems are created when engineering, design, intelligence, business understanding, and operational reality come together." },
      { icon: "Compass", text: "That is the mindset behind NGXP – where every decision is driven by what creates real, lasting value for the organizations we serve." },
    ],
  },
  thinking: {
    overline: "Our Approach",
    title_pre: "Our Thinking ",
    title_highlight: "Approach",
    paragraphs: [
      "NGXP is not built around one narrow technology category. We think in terms of business outcomes, system architecture, operational flow, and long-term value.",
      "This approach helps us build systems that are not only technically strong, but also meaningful to the people and businesses using them.",
    ],
    questions_label: "Before we build, we ask:",
    questions: [
      "What problem is the organization truly trying to solve?",
      "What decisions are currently delayed or unclear?",
      "Where is manual effort slowing the business?",
      "Where are risks, inefficiencies, or visibility gaps?",
      "What data already exists, and what is missing?",
      "How should the system support users, managers, and leadership?",
      "How can the solution continue to improve over time?",
    ],
  },
  team: {
    overline: "Our Team",
    title_pre: "A Multidisciplinary Team for ",
    title_highlight: "Complex Technology Challenges",
    paragraphs: [
      "NGXP is powered by a growing team of engineers, designers, developers, architects, 3D specialists, AI practitioners, IoT and embedded system professionals, and enterprise solution experts.",
      "Our strength is not just in having different technical skills. Our strength is in combining those skills to solve problems that require more than one discipline.",
      "Complex enterprise challenges often need many capabilities working together – data, software, intelligence, visualization, field systems, user experience, and operational logic.",
    ],
    footer: "That is where NGXP is built to perform.",
    skills: [
      "Artificial Intelligence", "Enterprise Systems", "Data Engineering", "IoT & Embedded Systems",
      "3D & Simulation", "XR & Digital Twins", "Solution Architecture", "User Experience",
    ],
  },
  "how-we-work": {
    overline: "How We Work",
    title_pre: "We work as a ",
    title_highlight: "strategic technology partner",
    paragraph:
      "Our process is built around clarity, alignment, execution, and continuous improvement – not only as a development vendor.",
    steps: [
      { num: "01", title: "Understand the Business Reality", desc: "We study the operational problem, business goal, user needs, workflow, constraints, and expected outcome." },
      { num: "02", title: "Define the Right System Strategy", desc: "We identify what kind of system is truly required – not just what features need to be developed." },
      { num: "03", title: "Engineer with Practicality", desc: "We design and build systems that are usable, scalable, secure, and aligned with real-world operations." },
      { num: "04", title: "Validate Through Outcomes", desc: "We focus on whether the solution improves visibility, speed, accuracy, safety, productivity, or decision-making." },
      { num: "05", title: "Improve and Scale", desc: "We help organizations evolve the system as needs grow, data improves, and operations mature." },
    ],
  },
  differentiators: {
    overline: "Our Difference",
    title_pre: "What Makes ",
    title_highlight: "NGXP Different",
    cards: [
      { title: "We Understand Both Technology and Operations", desc: "We do not look at projects only from a coding perspective. We study the operational problem behind the requirement." },
      { title: "We Combine Multiple Advanced Disciplines", desc: "Our team can bring together enterprise systems, AI, IoT, 3D, simulation, dashboards, automation, and user experience into one connected solution." },
      { title: "We Build for Business Value", desc: "Our focus is not only delivery. Our focus is helping clients achieve measurable improvements in visibility, efficiency, safety, productivity, and decision-making." },
      { title: "We Think Long Term", desc: "We design systems that can grow with the organization, adapt to future needs, and support continuous improvement." },
      { title: "We Are Ambitious About Quality", desc: "NGXP is built with the ambition to deliver technology at a level that can compete globally in thinking, engineering, design, and execution." },
    ],
  },
  commitment: {
    overline: "Our Commitment",
    title_pre: "Our Commitment ",
    title_highlight: "to Clients",
    paragraphs: [
      "We are committed to building systems that are practical, reliable, intelligent, and aligned with real business outcomes.",
      "We aim to become a trusted technology partner for organizations that want to move confidently into the next generation of intelligent operations.",
    ],
    items_label: "When clients work with NGXP, they can expect:",
    items: [
      "Clear understanding of business goals", "Strong technical ownership",
      "Thoughtful solution architecture", "Scalable engineering approach",
      "User-focused design thinking", "Transparent communication",
      "Long-term improvement mindset", "Commitment to measurable value",
    ],
  },
  future: {
    overline: "Our Future Direction",
    title_pre: "Where We Are ",
    title_highlight: "Going",
    left_paragraphs: [
      "NGXP is building toward a future where enterprises can operate with greater intelligence, clarity, and control.",
      "Our direction is focused on advanced enterprise intelligence, AI-native systems, connected operations, digital twins, immersive training, simulation-led decision-making, and intelligent command environments.",
    ],
    right_paragraph: "We are not only building for today's requirements.",
    right_emphasis:
      "We are building for the future of how organizations will operate, train, monitor, decide, and grow.",
  },
  "final-cta": {
    title: "Build the Future with NGXP",
    paragraph:
      "If your organization is looking for a technology partner who can understand complex challenges, engineer intelligent systems, and support long-term business transformation, NGXP Technologies is ready to collaborate.",
    cta_primary_label: "Explore Our Capabilities",
    cta_primary_link: "/services",
    cta_secondary_label: "Start a Strategic Discussion",
    cta_secondary_link: "/contact#contact-form",
    tagline:
      "NGXP Technologies - Built to engineer intelligent systems for the next generation of enterprise operations.",
  },
};

function buildLookup(sections) {
  const map = {};
  for (const s of sections) map[s.key] = s;
  const get = (key, field, fallback) => {
    const s = map[key];
    if (!s) return fallback;
    const v = s.content?.[field];
    if (v === undefined || v === null) return fallback;
    if (typeof v === "string" && v.trim() === "") return fallback;
    if (Array.isArray(v) && v.length === 0) return fallback;
    return v;
  };
  const enabled = (key) => {
    if (Object.keys(map).length === 0) return true; // fallback when API empty
    return !!map[key]?.enabled;
  };
  return { get, enabled };
}

export default function About() {
  const [sections, setSections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/pages/about/sections`).then(({ data }) => {
      if (Array.isArray(data)) setSections(data);
    }).catch(() => {});
  }, []);

  const { get, enabled } = buildLookup(sections);
  const F = FALLBACK;

  // Resolve all variables (concise)
  const hero = {
    overline: get("hero", "overline", F.hero.overline),
    pre: get("hero", "title_pre", F.hero.title_pre),
    hl: get("hero", "title_highlight", F.hero.title_highlight),
    post: get("hero", "title_post", F.hero.title_post),
    p1: get("hero", "paragraph1", F.hero.paragraph1),
    p2: get("hero", "paragraph2", F.hero.paragraph2),
    cpl: get("hero", "cta_primary_label", F.hero.cta_primary_label),
    cpu: get("hero", "cta_primary_link", F.hero.cta_primary_link),
    csl: get("hero", "cta_secondary_label", F.hero.cta_secondary_label),
    csu: get("hero", "cta_secondary_link", F.hero.cta_secondary_link),
    img: get("hero", "image_url", F.hero.image_url),
  };
  const wwa = {
    o: get("who-we-are", "overline", F["who-we-are"].overline),
    pre: get("who-we-are", "title_pre", F["who-we-are"].title_pre),
    hl: get("who-we-are", "title_highlight", F["who-we-are"].title_highlight),
    paragraphs: get("who-we-are", "paragraphs", F["who-we-are"].paragraphs),
    callout: get("who-we-are", "callout", F["who-we-are"].callout),
  };
  const orig = {
    o: get("origin", "overline", F.origin.overline),
    pre: get("origin", "title_pre", F.origin.title_pre),
    hl: get("origin", "title_highlight", F.origin.title_highlight),
    paragraphs: get("origin", "paragraphs", F.origin.paragraphs),
    quote: get("origin", "quote", F.origin.quote),
    closing: get("origin", "closing", F.origin.closing),
  };
  const vm = {
    vpre: get("vision-mission", "vision_title_pre", F["vision-mission"].vision_title_pre),
    vhl: get("vision-mission", "vision_title_highlight", F["vision-mission"].vision_title_highlight),
    vps: get("vision-mission", "vision_paragraphs", F["vision-mission"].vision_paragraphs),
    vfooter: get("vision-mission", "vision_footer", F["vision-mission"].vision_footer),
    mpre: get("vision-mission", "mission_title_pre", F["vision-mission"].mission_title_pre),
    mhl: get("vision-mission", "mission_title_highlight", F["vision-mission"].mission_title_highlight),
    mp: get("vision-mission", "mission_paragraph", F["vision-mission"].mission_paragraph),
    mlabel: get("vision-mission", "mission_label", F["vision-mission"].mission_label),
    mitems: get("vision-mission", "mission_items", F["vision-mission"].mission_items),
    mfooter: get("vision-mission", "mission_footer", F["vision-mission"].mission_footer),
  };
  const bel = {
    o: get("believe", "overline", F.believe.overline),
    pre: get("believe", "title_pre", F.believe.title_pre),
    hl: get("believe", "title_highlight", F.believe.title_highlight),
    post: get("believe", "title_post", F.believe.title_post),
    cards: get("believe", "cards", F.believe.cards),
  };
  const thk = {
    o: get("thinking", "overline", F.thinking.overline),
    pre: get("thinking", "title_pre", F.thinking.title_pre),
    hl: get("thinking", "title_highlight", F.thinking.title_highlight),
    paragraphs: get("thinking", "paragraphs", F.thinking.paragraphs),
    qlabel: get("thinking", "questions_label", F.thinking.questions_label),
    questions: get("thinking", "questions", F.thinking.questions),
  };
  const tm = {
    o: get("team", "overline", F.team.overline),
    pre: get("team", "title_pre", F.team.title_pre),
    hl: get("team", "title_highlight", F.team.title_highlight),
    paragraphs: get("team", "paragraphs", F.team.paragraphs),
    footer: get("team", "footer", F.team.footer),
    skills: get("team", "skills", F.team.skills),
  };
  const hw = {
    o: get("how-we-work", "overline", F["how-we-work"].overline),
    pre: get("how-we-work", "title_pre", F["how-we-work"].title_pre),
    hl: get("how-we-work", "title_highlight", F["how-we-work"].title_highlight),
    p: get("how-we-work", "paragraph", F["how-we-work"].paragraph),
    steps: get("how-we-work", "steps", F["how-we-work"].steps),
  };
  const diff = {
    o: get("differentiators", "overline", F.differentiators.overline),
    pre: get("differentiators", "title_pre", F.differentiators.title_pre),
    hl: get("differentiators", "title_highlight", F.differentiators.title_highlight),
    cards: get("differentiators", "cards", F.differentiators.cards),
  };
  const cm = {
    o: get("commitment", "overline", F.commitment.overline),
    pre: get("commitment", "title_pre", F.commitment.title_pre),
    hl: get("commitment", "title_highlight", F.commitment.title_highlight),
    paragraphs: get("commitment", "paragraphs", F.commitment.paragraphs),
    label: get("commitment", "items_label", F.commitment.items_label),
    items: get("commitment", "items", F.commitment.items),
  };
  const fut = {
    o: get("future", "overline", F.future.overline),
    pre: get("future", "title_pre", F.future.title_pre),
    hl: get("future", "title_highlight", F.future.title_highlight),
    left: get("future", "left_paragraphs", F.future.left_paragraphs),
    right: get("future", "right_paragraph", F.future.right_paragraph),
    emp: get("future", "right_emphasis", F.future.right_emphasis),
  };
  const cta = {
    title: get("final-cta", "title", F["final-cta"].title),
    p: get("final-cta", "paragraph", F["final-cta"].paragraph),
    cpl: get("final-cta", "cta_primary_label", F["final-cta"].cta_primary_label),
    cpu: get("final-cta", "cta_primary_link", F["final-cta"].cta_primary_link),
    csl: get("final-cta", "cta_secondary_label", F["final-cta"].cta_secondary_label),
    csu: get("final-cta", "cta_secondary_link", F["final-cta"].cta_secondary_link),
    tag: get("final-cta", "tagline", F["final-cta"].tagline),
  };

  return (
    <div data-testid="about-page">

      {enabled("hero") && (
      <section className="relative bg-white pt-6 md:pt-10 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#7B2CBF]/20 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="overline">{hero.overline}</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
                {hero.pre}<span className="gradient-text-glow">{hero.hl}</span>{hero.post}
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">{hero.p1}</p>
              <p className="mt-4 text-base text-gray-500 leading-relaxed max-w-2xl">{hero.p2}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={hero.cpu} className="btn-primary-gradient" data-testid="about-capabilities">
                  {hero.cpl} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to={hero.csu} className="btn-secondary-light" data-testid="about-cta">
                  {hero.csl} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-gray-100 shadow-2xl">
                <img src={hero.img} alt="Command center" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0B10]/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("who-we-are") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">{wwa.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                {wwa.pre}<span className="gradient-text-glow">{wwa.hl}</span>
              </h2>
            </div>
            <div className="lg:col-span-7 reveal space-y-5 text-gray-600 leading-relaxed">
              {wwa.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
                <p className="font-display text-lg font-semibold text-[#0A0B10]">{wwa.callout}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("origin") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-4xl mx-auto reveal">
            <span className="overline">{orig.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {orig.pre}<span className="gradient-text-glow">{orig.hl}</span>
            </h2>
            <div className="mt-8 space-y-5 text-gray-600 leading-relaxed text-lg">
              {orig.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              <p className="font-display text-xl font-semibold text-[#0A0B10] pl-5 border-l-4 border-[#7B2CBF]">{orig.quote}</p>
              <p>{orig.closing}</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("vision-mission") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="ngxp-card reveal space-y-4">
              <div className="icon-badge"><Eye className="w-6 h-6" /></div>
              <h2 className="font-display text-3xl font-bold text-[#0A0B10]">{vm.vpre}<span className="gradient-text-glow">{vm.vhl}</span></h2>
              {vm.vps.map((p, i) => <p key={i} className="text-gray-600 leading-relaxed">{p}</p>)}
              <p className="font-semibold text-[#0A0B10]">{vm.vfooter}</p>
            </div>
            <div className="ngxp-card reveal space-y-4">
              <div className="icon-badge"><Target className="w-6 h-6" /></div>
              <h2 className="font-display text-3xl font-bold text-[#0A0B10]">{vm.mpre}<span className="gradient-text-glow">{vm.mhl}</span></h2>
              <p className="text-gray-600 leading-relaxed">{vm.mp}</p>
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest">{vm.mlabel}</p>
              <div className="grid grid-cols-2 gap-2">
                {vm.mitems.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-4 h-4 text-[#7B2CBF] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 italic">{vm.mfooter}</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("believe") && (
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="glow-blob bg-[#7B2CBF]/30 w-[500px] h-[500px] top-0 right-0 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[400px] h-[400px] bottom-0 left-0 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">{bel.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {bel.pre}<span className="gradient-text-glow">{bel.hl}</span>{bel.post}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 reveal">
            {bel.cards.map((b, i) => {
              const Icon = ICONS[b.icon] || Heart;
              return (
                <div key={i} className="believe-card rounded-2xl border border-white/10 bg-white/5 p-6 space-y-3">
                  <div className="believe-icon w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed">{b.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {enabled("thinking") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">{thk.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                {thk.pre}<span className="gradient-text-glow">{thk.hl}</span>
              </h2>
              {thk.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? "mt-5 text-gray-600 leading-relaxed" : "mt-4 text-gray-600 leading-relaxed"}>{p}</p>
              ))}
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest mb-5">{thk.qlabel}</p>
              <div className="space-y-3">
                {thk.questions.map((q, i) => (
                  <div key={i} className="thinking-card flex items-start gap-3 p-4 rounded-xl bg-[#F8F9FA] border border-gray-100" style={{ animationDelay: `${i * 0.08}s` }}>
                    <span className="text-xs font-mono font-bold text-[#7B2CBF] mt-0.5 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-gray-700 leading-relaxed">{q}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("team") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">{tm.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
              {tm.pre}<span className="gradient-text-glow">{tm.hl}</span>
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6 reveal space-y-5 text-gray-600 leading-relaxed">
              {tm.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              <p className="font-semibold text-[#0A0B10]">{tm.footer}</p>
            </div>
            <div className="lg:col-span-6 reveal">
              <div className="grid grid-cols-2 gap-4">
                {tm.skills.map((skill, i) => (
                  <div key={i} className="skill-card flex items-center gap-2 p-3 rounded-xl bg-white border border-gray-100 shadow-sm" style={{ animationDelay: `${i * 0.1}s` }}>
                    <span className="skill-dot w-2 h-2 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F] flex-shrink-0" />
                    <span className="skill-label text-sm font-medium text-[#0A0B10]">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("how-we-work") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">{hw.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {hw.pre}<span className="gradient-text-glow">{hw.hl}</span>
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed">{hw.p}</p>
          </div>
          <div className="mt-12 space-y-5 reveal">
            {hw.steps.map((s, i) => (
              <div key={i} className="work-step-card grid grid-cols-1 md:grid-cols-12 gap-4 items-start p-6 rounded-2xl bg-[#F8F9FA] border border-gray-100" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="md:col-span-1">
                  <span className="work-step-num font-mono text-2xl font-bold text-[#7B2CBF]">{s.num}</span>
                </div>
                <div className="md:col-span-11">
                  <h3 className="font-display text-lg font-semibold text-[#0A0B10]">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {enabled("differentiators") && (
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">{diff.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              {diff.pre}<span className="gradient-text-glow">{diff.hl}</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal">
            {diff.cards.map((d, i) => (
              <div key={i} className="ngxp-card space-y-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-bold text-[#7B2CBF]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-base font-semibold text-[#0A0B10] leading-snug">{d.title}</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {enabled("commitment") && (
      <section className="bg-white py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 reveal">
              <span className="overline">{cm.o}</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10] leading-tight">
                {cm.pre}<span className="gradient-text-glow">{cm.hl}</span>
              </h2>
              {cm.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? "mt-5 text-gray-600 leading-relaxed" : "mt-4 text-gray-600 leading-relaxed"}>{p}</p>
              ))}
            </div>
            <div className="lg:col-span-7 reveal">
              <p className="text-sm font-semibold text-[#0A0B10] uppercase tracking-widest mb-5">{cm.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {cm.items.map((item, i) => (
                  <div key={i} className="commitment-card flex items-center gap-3 p-4 rounded-xl bg-[#F8F9FA] border border-gray-100" style={{ animationDelay: `${i * 0.07}s` }}>
                    <CheckCircle2 className="commitment-icon w-5 h-5 text-[#7B2CBF] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("future") && (
      <section className="bg-[#0A0B10] text-white py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="glow-blob bg-[#FF007F]/20 w-[500px] h-[500px] -top-40 -right-32 pulse-glow" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[400px] h-[400px] bottom-0 left-0 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="max-w-3xl reveal">
            <span className="overline">{fut.o}</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              {fut.pre}<span className="gradient-text-glow">{fut.hl}</span>
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 reveal">
            <div className="space-y-5 text-white/80 leading-relaxed">
              {fut.left.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="space-y-5 text-white/80 leading-relaxed">
              <p>{fut.right}</p>
              <p className="font-display text-xl text-white font-semibold">{fut.emp}</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {enabled("final-cta") && (
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="rounded-3xl bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-30" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 items-center gap-8">
              <div className="lg:col-span-8">
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{cta.title}</h3>
                <p className="mt-3 text-white/85 max-w-2xl leading-relaxed">{cta.p}</p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end flex-wrap gap-3">
                <Link to={cta.cpu} data-testid="about-capabilities-cta" className="inline-flex items-center gap-2 rounded-full bg-white/20 border border-white/30 text-white px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  {cta.cpl} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to={cta.csu} data-testid="about-final-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform">
                  {cta.csl} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <p className="relative z-10 mt-8 text-white/50 text-xs text-center">{cta.tag}</p>
          </div>
        </div>
      </section>
      )}

    </div>
  );
}
